/**
 * Catálogo musical inicial — conteúdo PADRÃO do Compasso, carregado no boot
 * (todos os ambientes) pra a família não começar com a biblioteca vazia.
 *
 * Repertório enxuto, calmo e culturalmente próximo (PT-BR): canções de rotina,
 * instrumentos simples de percussão/melódicos e atividades de movimento. Cada item
 * nasce sem mídia (emoji por nome/tipo) e com status "não usada" (honesto). Id
 * estável (`mus-<slug>`) pra ser idempotente entre boots.
 */
import type { ExperienciaMusical, PerfilSonoro } from "./types";

const SONORO_VAZIO: PerfilSonoro = {
  andamento: null,
  intensidade: null,
  timbre: null,
  previsibilidade: null,
  familiaridade: null,
};

type Semente = Pick<ExperienciaMusical, "nome" | "tipo" | "funcao" | "modalidade"> & {
  sonoro?: Partial<PerfilSonoro>;
};

const SEMENTES: Semente[] = [
  {
    nome: "canção do oi",
    tipo: "cancao",
    funcao: "conectar",
    modalidade: ["ouvir", "cantar"],
    sonoro: { andamento: "moderado", intensidade: "suave", previsibilidade: "alta", familiaridade: "conhecida" },
  },
  {
    nome: "canção do tchau",
    tipo: "cancao",
    funcao: "comunicar",
    modalidade: ["ouvir", "cantar"],
    sonoro: { andamento: "moderado", intensidade: "suave", previsibilidade: "alta", familiaridade: "conhecida" },
  },
  {
    nome: "tambor",
    tipo: "instrumento",
    funcao: "ativar",
    modalidade: ["tocar", "mover"],
    sonoro: { andamento: "moderado", intensidade: "forte", timbre: "percussivo / madeira", previsibilidade: "media", familiaridade: "conhecida" },
  },
  {
    nome: "chocalho",
    tipo: "instrumento",
    funcao: "conectar",
    modalidade: ["tocar"],
    sonoro: { andamento: "rapido", intensidade: "media", timbre: "metálico / sementes", previsibilidade: "media", familiaridade: "conhecida" },
  },
  {
    nome: "sino / sininho",
    tipo: "instrumento",
    funcao: "comunicar",
    modalidade: ["ouvir", "tocar"],
    sonoro: { andamento: "lento", intensidade: "suave", timbre: "metálico", previsibilidade: "alta", familiaridade: "conhecida" },
  },
  {
    nome: "violão",
    tipo: "instrumento",
    funcao: "conectar",
    modalidade: ["ouvir"],
    sonoro: { andamento: "moderado", intensidade: "media", timbre: "cordas", previsibilidade: "media", familiaridade: "conhecida" },
  },
  {
    nome: "improviso com a voz",
    tipo: "improviso",
    funcao: "comunicar",
    modalidade: ["cantar", "ouvir"],
    sonoro: { andamento: "moderado", intensidade: "suave", timbre: "voz", previsibilidade: "baixa", familiaridade: "nova" },
  },
  {
    nome: "playlist do banho",
    tipo: "playlist",
    funcao: "regular",
    modalidade: ["ouvir"],
    sonoro: { andamento: "lento", intensidade: "suave", previsibilidade: "alta", familiaridade: "favorita" },
  },
  {
    nome: "playlist da soneca",
    tipo: "playlist",
    funcao: "regular",
    modalidade: ["ouvir"],
    sonoro: { andamento: "lento", intensidade: "suave", previsibilidade: "alta", familiaridade: "favorita" },
  },
  {
    nome: "dança das músicas",
    tipo: "atividade",
    funcao: "motor",
    modalidade: ["mover", "ouvir"],
    sonoro: { andamento: "rapido", intensidade: "forte", previsibilidade: "media", familiaridade: "conhecida" },
  },
];

const slug = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export function catalogoMusicalInicial(): ExperienciaMusical[] {
  const agora = new Date().toISOString();
  return SEMENTES.map((s) => ({
    id: `mus-${slug(s.nome)}`,
    nome: s.nome,
    tipo: s.tipo,
    midias: [],
    funcao: s.funcao,
    sonoro: { ...SONORO_VAZIO, ...s.sonoro },
    modalidade: s.modalidade,
    status: "nao_usada" as const,
    observacoes: null,
    arquivado: false,
    criado_em: agora,
  }));
}
